import _ from 'underscore';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { getHotelCenterUtils } from '@bingads-webui-campaign/hotel-center-utils';
import template from './bid-multipliers-view-template.pug';
import { getUserCountryMultipliersView } from './user-country/view';
import { getDeviceTypeMultipliersView } from './device-type/view';
import { getCheckInDayMultipliersView } from './check-in-day/view';
import { getLengthOfStayMultipliersView } from './length-of-stay/view';
import { getBidMultiplierView } from './single-multiplier-view/view';

export const getBidMultipliersView = ({
  i18n, state: componentState, pageContext, multiplierSchema,
}) => {
  if (!i18n || !componentState || !pageContext || !multiplierSchema) {
    return {};
  }
  const { bidMultiplierUtils } = getHotelCenterUtils({ i18n, state: componentState });
  const { getMultipliersState } = bidMultiplierUtils;
  const { CheckInDayMultipliersView } =
    getCheckInDayMultipliersView({
      i18n, state: componentState, pageContext, multiplierSchema,
    });
  const { DeviceTypeMultipliersView } =
    getDeviceTypeMultipliersView({
      i18n, state: componentState, pageContext, multiplierSchema,
    });
  const { LengthOfStayMultipliersView } =
    getLengthOfStayMultipliersView({
      i18n, state: componentState, pageContext, multiplierSchema,
    });
  const { BidMultiplierView } =
    getBidMultiplierView({ i18n, pageContext, multiplierSchema });
  const { UserCountryMultipliersView } =
    getUserCountryMultipliersView({
      i18n, state: componentState, pageContext, multiplierSchema,
    });
  /**
   * Shared view for Hotel Ads Bid Multipliers
   * @extends DeterministicView
   */
  const deviceTypes = ['Mobile', 'Tablet', 'Desktop'];
  const checkInDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  class BidMultipliersView extends DeterministicView {
    initialize({
      showUseParent = true,
      isPayPerStay = false,
      isPayPerStayPhase2Pilot = false,
    }) {
      this.i18n = i18n;
      this.showUseParent = showUseParent;
      this.deviceTypeMultipliersView = new DeviceTypeMultipliersView();
      this.dateTypeMultipliersView = new DeviceTypeMultipliersView({
        listOfTypes: {
          Selected: 'HotelCenter_Overview_DateType_Multipliers_Selected_Label',
          Default: 'HotelCenter_Overview_DateType_Multipliers_Default_Label',
        },
        type: 'dateType',
      });
      this.isHotelPropertyPromotionAdEnabled =
        pageContext.Permissions.IsHotelPropertyPromotionAdEnabled;
      this.siteTypeMultipliersView = new DeviceTypeMultipliersView({
        listOfTypes: {
          LocalUniversal: 'HotelCenter_Overview_SiteType_Multipliers_Local_Universal_Label',
          MapResults: 'HotelCenter_Overview_SiteType_Multipliers_Map_Results_Label',
          ...(this.isHotelPropertyPromotionAdEnabled ? { PropertyPromotionAd: 'HotelCenter_Overview_SiteType_Multipliers_Property_PromotionAd_Label' } : {}),
        },
        type: 'siteType',
      });
      this.userCountryMultipliersView = new UserCountryMultipliersView();
      this.lengthOfStayMultipliersView = new LengthOfStayMultipliersView();
      this.advancedBookingMultipliersView = new LengthOfStayMultipliersView({
        minNumberOfNights: 0,
        maxNumberOfNights: 180,
        type: 'advancedBooking',
      });
      this.checkInDayMultipliersView = new CheckInDayMultipliersView();
      this.setChild('device-type-multipliers', this.deviceTypeMultipliersView);
      this.setChild('date-type-multipliers', this.dateTypeMultipliersView);
      this.setChild('site-type-multipliers', this.siteTypeMultipliersView);
      this.setChild('user-country-multipliers', this.userCountryMultipliersView);
      this.setChild('length-of-stay-multipliers', this.lengthOfStayMultipliersView);
      this.setChild('check-in-day-multipliers', this.checkInDayMultipliersView);
      this.setChild('advanced-booking-multipliers', this.advancedBookingMultipliersView);
      this.setState({
        selectedMultiplierType: 'deviceType',
        isPayPerStay,
        isPayPerStayPhase2Pilot,
      });
      this.setState({ errors: [] });
    }

    get template() {
      return template;
    }

    get events() {
      return {
        'click .bid-multipliers-panel li': ({ target }) => {
          this.setState({
            selectedMultiplierType: target.dataset.type,
          });
        },
        'click .bid-multipliers-use-parent input[type=checkbox]': ({ target }) => {
          const isUseParent = target.checked;

          this.setState({ isUseParent });
          this.trigger('update:isUseParent', isUseParent);
        },
      };
    }

    setState({
      selectedMultiplierType,
      bidMultipliers,
      isUseParent,
      errors,
      isPayPerStay,
      isPayPerStayPhase2Pilot,
    }) {
      if (!_.isUndefined(isPayPerStay)) {
        this.model.set({ isPayPerStay });
      }
      if (!_.isUndefined(isPayPerStayPhase2Pilot)) {
        this.model.set({ isPayPerStayPhase2Pilot });
      }

      if (!_.isUndefined(isUseParent)) {
        this.model.set({ isUseParent });
        this.getChild('device-type-multipliers').setState({ isUseParent });
        this.getChild('date-type-multipliers').setState({ isUseParent });
        this.getChild('site-type-multipliers').setState({ isUseParent });
        this.getChild('user-country-multipliers').setState({ isUseParent });
        this.getChild('check-in-day-multipliers').setState({ isUseParent });
        this.getChild('length-of-stay-multipliers').setState({ isUseParent });
        this.getChild('advanced-booking-multipliers').setState({ isUseParent });
      }
      if (selectedMultiplierType && !this.model.get('isUseParent')) {
        this.model.set({ selectedMultiplierType });
      }
      if (!_.isEmpty(bidMultipliers)) {
        // set deviceType
        this.getChild('device-type-multipliers').setState(bidMultipliers.deviceType);

        // set dateType
        this.getChild('date-type-multipliers').setState(bidMultipliers.dateType);

        // set siteType
        this.getChild('site-type-multipliers').setState(bidMultipliers.siteType);

        // set userCountry
        const state = this.userCountryMultipliersView
          .getSelectedCountriesMulti(bidMultipliers.userCountry || {});

        this.getChild('user-country-multipliers').setState(state);

        // set checkInDay
        this.getChild('check-in-day-multipliers').setState(bidMultipliers.checkInDay);

        // set lengthOfStay
        this.getChild('length-of-stay-multipliers').setState({ staySegments: bidMultipliers.lengthOfStay || [] });

        // set advancedBooking
        this.getChild('advanced-booking-multipliers').setState({ staySegments: bidMultipliers.advancedBooking || [] });

        this.model.set({ selectedMultiplierType: this.model.attributes.selectedMultiplierType });
      }

      if (errors) {
        this.model.set({ errors });
      }

      return this;
    }

    getBidMultipliers(bidMultipliers) {
      let res = [];

      _.each(bidMultipliers, (item) => {
        res = res.concat(item.bidMultipliers);
      });
      return res;
    }

    getState() {
      const isUseParent = this.model.get('isUseParent');
      const bidMultipliers = [];

      if (isUseParent) {
        return {
          isValid: true,
          bidMultipliers: [],
        };
      }

      bidMultipliers.push(this.getChild('device-type-multipliers').getState());
      bidMultipliers.push(this.getChild('date-type-multipliers').getState());
      bidMultipliers.push(this.getChild('site-type-multipliers').getState());
      bidMultipliers.push(this.getChild('user-country-multipliers').getState());
      bidMultipliers.push(this.getChild('check-in-day-multipliers').getState());
      bidMultipliers.push(this.getChild('length-of-stay-multipliers').getState());
      bidMultipliers.push(this.getChild('advanced-booking-multipliers').getState());
      const errors = _.chain(bidMultipliers)
        .pluck('errors')
        .flatten()
        .compact()
        .value();
      this.setState({ errors });

      if (_.findWhere(bidMultipliers, { isValid: false })) {
        return {
          isValid: false,
          bidMultipliers: _.noop(),
        };
      }

      return {
        isValid: true,
        bidMultipliers: this.getBidMultipliers(bidMultipliers),
      };
    }

    clean() {
      const state = this.getState();
      let bidMultipliers;

      if (state.isValid) {
        bidMultipliers = getMultipliersState(state.bidMultipliers, true);
      }

      // to set mobile as 0 to in case bid multipliers covered by its father node
      // when there is no data
      if (!bidMultipliers || (_.isEmpty(bidMultipliers.checkInDay) &&
          _.isEmpty(bidMultipliers.deviceType) &&
          _.isEmpty(bidMultipliers.lengthOfStay) &&
          _.isEmpty(bidMultipliers.userCountry) &&
          _.isEmpty(bidMultipliers.dateType) &&
          _.isEmpty(bidMultipliers.siteType) &&
          _.isEmpty(bidMultipliers.advancedBooking))) {
        _.extend(bidMultipliers, {
          deviceType: {
            Mobile: {
              factor: 0,
            },
          },
        });
      }

      this.setState({ bidMultipliers });
    }

    emptyDeviceTypeAndCheckInDay() {
      const bidMultipliers = {
        deviceType: _.reduce(deviceTypes, (memo, item) =>
          _.extend(memo, {
            [item]: { factor: '' },
          }), {}),
        checkInDay: _.reduce(checkInDays, (memo, item) =>
          _.extend(memo, {
            [item]: { factor: '' },
          }), {}),
      };

      this.setState({ bidMultipliers });
    }
  }

  return {
    BidMultipliersView,
    BidMultiplierView,
    UserCountryMultipliersView,
    DeviceTypeMultipliersView,
    CheckInDayMultipliersView,
    LengthOfStayMultipliersView,
  };
};
